import { useCallback, useEffect, useState } from 'react';
import {
  getPlatformConfig,
  updatePlatformParam,
  type PlatformParam,
} from '../../lib/nexa-config-api';
import { AvailabilityGateBoard } from './AvailabilityGateBoard';
import { useStaffMe } from '../../hooks/useStaffMe';
import { ZoneSelector, ZoneScopeNote, useZoneSelection } from '../../components/ZoneSelector';
import { type PortalZone } from '../../lib/zones';
import { useEffectiveTimeZone, formatInstant } from '../../lib/time-format';
import { StaffApiError } from '../../lib/api-client';

/**
 * /admin/config — "Nexa Config" (Phase 5d-5 / 5c, ADR-046 §6.4; ADR-080 Am.1;
 * Punchlist #9 A).
 *
 * Two in-page tabs:
 *   - Parameters        — the report-pipeline runtime knobs (reportWindowDays,
 *                         minimumFullMonths). Changes take effect on the next
 *                         report run (no redeploy) and are audit-logged.
 *   - Availability Gate — TWO INDEPENDENT kill-switches, one card each:
 *                         · Public sign-up gate (ADR-080 Am.1) — the whole
 *                           unauthenticated public sign-up journey.
 *                         · Beta invites gate (Punchlist #9 A) — partner beta
 *                           onboarding (Start BOW).
 *                         Separately controllable: closing one never moves the
 *                         other. super_admin / nexa_admin toggle both.
 *
 * The old "Beta Testing" tab (one-shot beta-site provisioning) was removed in
 * the ADR-080 Am.1 build — it is superseded by the Start BOW dashboard workflow.
 *
 * ADR-122 Pattern 3 — THE PARAMETERS TAB IS A ZONE-SCOPED WRITE SURFACE, SO IT SELECTS A ZONE. The
 * zone selector governs the Parameters tab only: each zone holds its own parameters, and the
 * navigation bar names the selected zone as the write target (ADR-123 Decision 1).
 *
 * ⭐⭐ THE AVAILABILITY GATE TAB IS ONE PAGE FOR EVERY ZONE (G1/G2, Jeff, 4–5 October 2026) —
 * `AvailabilityGateBoard`. It replaced per-zone tabs that showed the platform-wide switch on every
 * zone's tab and an "other zones" panel, and showed nothing of what the public would meet: opening
 * the platform switch to test the US silently opened Australia too. The board shows the platform
 * emergency stop on top, one row per zone × gate with the EFFECTIVE state, the Customers page's
 * zone filter, and confirmations that name the zone — and, for the emergency stop, list every zone
 * that would change. Each write names its row's zone; the zone selector plays no part there.
 *
 * The tab strip reuses the established in-page button-tab pattern
 * (`portal-partners-tabs` / `portal-partners-tab`), not a new component (failure mode #11).
 */

type TabId = 'parameters' | 'gate';

export function NexaConfigPage() {
  const meState = useStaffMe();
  const role = meState.status === 'loaded' ? meState.me.role : undefined;
  // UAT fix: platform-config write is operational (config:manage_platform = {nexa_admin, super_admin}).
  const canEditParams = role === 'super_admin' || role === 'nexa_admin';
  // The gate toggle rides the same config:manage_platform gate as the params.
  const canToggleGate = role === 'super_admin' || role === 'nexa_admin';

  const [tab, setTab] = useState<TabId>('parameters');
  // ADR-122 Pattern 3 — default per ADR-123 Decision 3's precedence; see useZoneSelection.
  // The selector governs the Parameters tab only — the gate board names each row's zone itself,
  // so on that tab the navigation bar is not told a single-zone write is in play.
  const { zone, zoneCode, setZoneCode } = useZoneSelection('nexa-config', { reportWrite: tab === 'parameters' });

  return (
    <main className="monitoring-page">
      <h1>Nexa Config</h1>
      <p className="crumb">Admin → COG → Nexa Config</p>

      {/* The selector governs the Parameters tab only; the gate board covers every zone at once. */}
      {tab === 'parameters' && (
        <>
          <ZoneSelector value={zoneCode} onChange={setZoneCode} scopeLabel="configuration" />
          <ZoneScopeNote zone={zone} noun="Parameters" />
        </>
      )}

      <nav className="portal-partners-tabs" aria-label="Nexa Config sections">
        <button
          type="button"
          className={`portal-partners-tab${tab === 'parameters' ? ' active' : ''}`}
          aria-current={tab === 'parameters' ? 'page' : undefined}
          onClick={() => setTab('parameters')}
        >
          Parameters
        </button>
        <button
          type="button"
          className={`portal-partners-tab${tab === 'gate' ? ' active' : ''}`}
          aria-current={tab === 'gate' ? 'page' : undefined}
          onClick={() => setTab('gate')}
        >
          Availability Gate
        </button>
      </nav>

      {/* ⭐ `key={zone.code}` REMOUNTS THE PARAMETERS TAB ON A ZONE SWITCH, WHICH IS THE POINT.
          Pattern 3 requires the previous zone's values to be REPLACED, never left stale behind a
          new label. */}
      {tab === 'parameters' && <ParametersTab key={zone.code} canEdit={canEditParams} zone={zone} />}
      {tab === 'gate' && <AvailabilityGateBoard canToggle={canToggleGate} />}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Parameters tab — the original Nexa Config page content.
// ---------------------------------------------------------------------------

/**
 * Operator-facing COPY for each parameter — the one thing about a parameter that is a UI
 * concern rather than a server fact.
 *
 * ⚠ EVERYTHING ELSE NOW COMES FROM THE SERVER. This constant used to carry `value`, `min` and
 * `max` too, and the tab rendered them directly — which is why the card showed `5` and `1`
 * whether or not those were the stored values, and showed the same pair for both zones. The
 * numbers are read from `GET /admin/config/platform` per zone; only the human description
 * lives here, keyed by the server's own parameter name so a param the server stops returning
 * simply stops rendering rather than appearing with stale numbers.
 */
const PARAM_COPY: Record<string, string> = {
  reportWindowDays:
    'Days to spread monthly report generation across — flattens demand on external vendors.',
  minimumFullMonths:
    'Full telemetry months required before Snapshot intelligence activates for a domain.',
  invoiceRetentionYears:
    'Years a deleted account’s invoices are kept, from the invoice date, before the monthly purge removes them. Never less than 7.',
};

type ParamsState =
  | { status: 'loading' }
  | { status: 'loaded'; params: PlatformParam[] }
  /** `forbidden` is a DISTINCT outcome from a failed read — see the note on the render below. */
  | { status: 'error'; forbidden: boolean };

function ParametersTab({ canEdit, zone }: { canEdit: boolean; zone: PortalZone }) {
  const [state, setState] = useState<ParamsState>({ status: 'loading' });
  const [refOpen, setRefOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const tz = useEffectiveTimeZone();

  /**
   * ⚠ THE TAB IS REMOUNTED ON A ZONE SWITCH (`key={zone.code}` on the parent), so this runs
   * once per zone and the previous zone's values are destroyed rather than replaced. That is
   * Pattern 3's requirement, not an incidental effect: a stale value surviving under a new
   * zone heading is the failure this page exists to remove.
   */
  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const { params } = await getPlatformConfig(zone);
      setState({ status: 'loaded', params });
    } catch (e) {
      console.error('nexa-config: platform parameters read failed', e);
      // A 403 is not a broken page — it is `nexa_support` on a read pinned to the PUT's role
      // set. Telling them "could not read" would send them chasing an outage that is not there.
      // The STATUS, never a regex over the message — `StaffApiError` carries it (a message
      // match would also fire on a body that merely contained "403").
      const forbidden = e instanceof StaffApiError && e.status === 403;
      setState({ status: 'error', forbidden });
    }
  }, [zone]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * ⭐ A SAVE REFRESHES FROM THE SERVER RATHER THAN PATCHING LOCAL STATE.
   *
   * The old tab applied the new value to its own array and stamped `updatedAt` with the
   * browser's clock — so the card after an edit showed what the client BELIEVED it had written,
   * which is indistinguishable from what it had actually written and is the same class of
   * defect as the hardcoded constant. Re-reading costs one request and makes the card a report
   * of the table.
   */
  const onSaved = () => { void load(); };

  return (
    <>
      <button type="button" className="ncfg-ref" onClick={() => setRefOpen((v) => !v)}>
        {refOpen ? '▾' : '▸'} Parameter reference
      </button>
      {refOpen && (
        <div className="ncfg-refbody">
          Runtime parameters read by this zone at each run — the report pipeline's, and the invoice retention
          the monthly purge applies. Changes take effect on the next scheduled run — no redeploy. Every change requires a reason and is written to the audit
          trail.
        </div>
      )}

      {/* ⛔ NO VALUE IS RENDERED UNTIL ONE HAS BEEN READ. The loading and error branches below
          show the cards' STRUCTURE without numbers, never a placeholder digit — a plausible
          `5` on screen is precisely how this tab spent its life reporting a value nobody had
          read. The skeleton conveys the shape, the status line conveys the state. */}
      {state.status === 'loading' && (
        <div className="ncfg-grid" aria-busy="true">
          {Object.keys(PARAM_COPY).map((name) => (
            <div className="ncfg-card" key={name}>
              <div className="ncfg-k">{name}</div>
              <div className="ncfg-desc">{PARAM_COPY[name]}</div>
              <div className="ncfg-valrow">
                <span className="ncfg-val">…</span>
              </div>
              <div className="ncfg-audit">Loading current values…</div>
            </div>
          ))}
        </div>
      )}

      {state.status === 'error' && (
        <div className="mon-err" role="alert">
          {/* ⭐ TWO DIFFERENT SENTENCES, BECAUSE THEY ASK FOR DIFFERENT ACTIONS. "Retry shortly"
              sends a nexa_support operator chasing an outage that does not exist; "your role
              cannot see these" tells them the truth and stops them looking. */}
          {state.forbidden
            ? 'Current values are visible to super_admin and nexa_admin only — the same access needed to change them.'
            : 'Could not read the current values. They may be temporarily unavailable — retry shortly.'}
        </div>
      )}

      {state.status === 'loaded' && (
        <div className="ncfg-grid">
          {state.params.map((p) => (
            <div className="ncfg-card" key={p.name}>
              <div className="ncfg-k">{p.name}</div>
              <div className="ncfg-desc">{PARAM_COPY[p.name] ?? ''}</div>
              <div className="ncfg-valrow">
                <span className="ncfg-val">{p.value}</span>
                <span className="ncfg-range">
                  range {p.min}–{p.max}
                </span>
              </div>
              <div className="ncfg-audit">
                {/* ⭐ THREE DISTINCT STATES, NEVER COLLAPSED. "Never set" is not "unchanged" and
                    is not "changed at an unknown time": an unseeded param means the pipeline is
                    running on its built-in default, which an operator reading a bare number
                    would have no way to know. */}
                {p.source === 'default'
                  ? `Never set — the platform is using its default of ${p.default}`
                  : p.updatedAt
                    ? `Changed ${formatInstant(p.updatedAt, tz, 'datetime').text}${
                        p.updatedBy ? ` by ${p.updatedBy}` : ''
                      }`
                    : 'Stored — no change timestamp recorded'}
              </div>
              {p.source === 'persisted' && p.updatedReason && (
                <div className="ncfg-desc">Reason: {p.updatedReason}</div>
              )}
              <button
                type="button"
                className="mon-btn mon-btn-sm"
                disabled={!canEdit}
                title={canEdit ? undefined : 'super_admin or nexa_admin only'}
                onClick={() => setEditing(p.name)}
              >
                ✎ Edit
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && state.status === 'loaded' && (
        <EditParamModal
          param={state.params.find((p) => p.name === editing)!}
          zone={zone}
          onClose={() => setEditing(null)}
          onSaved={() => {
            onSaved();
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

function EditParamModal({
  param,
  zone,
  onClose,
  onSaved,
}: {
  param: PlatformParam;
  /** ADR-122 Pattern 3 — the zone this write targets. Never implicit. */
  zone: PortalZone;
  onClose: () => void;
  /** Fired on a successful write. Carries NO value: the tab re-reads the server rather than
   *  trusting what it believes it wrote. */
  onSaved: () => void;
}) {
  const [value, setValue] = useState(String(param.value));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    const n = Number(value);
    if (Number.isNaN(n) || n < param.min || n > param.max) {
      setErr(`Value must be between ${param.min} and ${param.max}.`);
      return;
    }
    if (!reason.trim()) {
      setErr('A reason is required.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await updatePlatformParam(param.name, n, reason, zone);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mon-mback" onClick={onClose}>
      <div className="mon-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="mon-modal-h">Edit {param.name}</h3>
        {/* Named in the modal too, not only in the page chrome: the operator confirming a
            write should not have to remember what the selector said. */}
        <ZoneScopeNote zone={zone} noun="Values" />
        <label>
          New value (range {param.min}–{param.max})
          <input type="number" value={value} min={param.min} max={param.max} onChange={(e) => setValue(e.target.value)} />
        </label>
        <label>
          Reason (required — audit trail)
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this changing?" />
        </label>
        {err && <div className="mon-err">{err}</div>}
        <div className="mon-foot">
          <button type="button" className="mon-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="mon-btn mon-btn-primary" disabled={busy} onClick={save}>
            {busy ? 'Saving…' : 'Save change'}
          </button>
        </div>
      </div>
    </div>
  );
}
